/**
 * lib/admissions.ts
 * ------------------------------------------------------------------
 * Pure helpers for the Admissions module (Feature E).
 *
 * - Admission number generation (LKC prefix, auto-increment).
 * - getPrimaryContact: single function every downstream feature
 *   calls to resolve "who do we email/WhatsApp for this student?"
 *   Checks admissions.guardians first, falls back to legacy
 *   parentContact, so existing BulkSend/BulkImport keep working
 *   with zero migration.
 * - Guardian validation helpers.
 *
 * All functions are pure (no IO) and unit-testable.
 * ------------------------------------------------------------------
 */
import type { Student, Guardian } from '@/types';

/* ==================================================================
   ADMISSION NUMBER GENERATION
   ==================================================================

   Format: LKC + 6-digit zero-padded number.
   The system reads the highest existing admission number across all
   students, increments by 1, and returns the next number.

   Starting assumption: if no students have admission numbers (or
   none match the LKC prefix), start from LKC200001 (the school's
   current highest is LKC200000).
   ================================================================== */

const ADMISSION_PREFIX = 'LKC';
const ADMISSION_DIGITS = 6;
const ADMISSION_FLOOR = 200000; // start from here if no existing numbers

/**
 * Parse the numeric part from an admission number like "LKC200042".
 * Returns null if the format doesn't match.
 */
export function parseAdmissionNumber(admNo: string | undefined | null): number | null {
  if (!admNo) return null;
  const upper = admNo.trim().toUpperCase();
  if (!upper.startsWith(ADMISSION_PREFIX)) return null;
  const numPart = upper.slice(ADMISSION_PREFIX.length);
  const n = parseInt(numPart, 10);
  return Number.isNaN(n) ? null : n;
}

/**
 * Format a numeric admission number back to the string form.
 * e.g. 200042 -> "LKC200042"
 */
export function formatAdmissionNumber(n: number): string {
  return `${ADMISSION_PREFIX}${String(n).padStart(ADMISSION_DIGITS, '0')}`;
}

/**
 * Scan all students and return the next admission number.
 * Pure function: same students array -> same result.
 */
export function getNextAdmissionNumber(students: Student[]): string {
  let max = ADMISSION_FLOOR;
  for (const s of students) {
    const n = parseAdmissionNumber(s.admissionNumber);
    if (n !== null && n > max) max = n;
  }
  return formatAdmissionNumber(max + 1);
}

/**
 * Check whether an admission number is already taken.
 * Case-insensitive comparison.
 */
export function isAdmissionNumberTaken(
  admNo: string,
  students: Student[],
  excludeStudentId?: string,
): boolean {
  const upper = admNo.trim().toUpperCase();
  return students.some(
    (s) =>
      s.id !== excludeStudentId &&
      s.admissionNumber?.trim().toUpperCase() === upper,
  );
}

/* ==================================================================
   PRIMARY CONTACT RESOLUTION
   ==================================================================

   Single function that every downstream feature (BulkSend, reports,
   parent accounts) should call to resolve "who is this student's
   primary contact?"

   Priority:
     1. admissions.guardians — find the one with isPrimaryContact
     2. Legacy parentContact — backward compat for pre-admissions data
     3. null — no contact info at all
   ================================================================== */

export interface ResolvedContact {
  name: string;
  email: string;
  whatsapp: string;
  phone: string;
  consentToSend: boolean;
  consentToWhatsapp: boolean;
  /** 'admissions' if resolved from guardians, 'legacy' from parentContact */
  source: 'admissions' | 'legacy';
}

/**
 * Resolve the primary contact for a student.
 * Returns null if no contact information exists at all.
 */
export function getPrimaryContact(student: Student): ResolvedContact | null {
  // 1. Try admissions guardians first
  const guardians = student.admissions?.guardians;
  if (guardians?.length) {
    const primary = guardians.find((g) => g.isPrimaryContact) ?? guardians[0];
    if (primary.fullName || primary.email || primary.phone || primary.whatsapp) {
      return {
        name: primary.fullName || '',
        email: primary.email || '',
        whatsapp: primary.whatsapp || '',
        phone: primary.phone || '',
        consentToSend: primary.consentToSend ?? false,
        consentToWhatsapp: primary.consentToWhatsapp ?? false,
        source: 'admissions',
      };
    }
  }

  // 2. Fall back to legacy parentContact
  const pc = student.parentContact;
  if (pc && (pc.guardianName || pc.email || pc.whatsapp)) {
    return {
      name: pc.guardianName || '',
      email: pc.email || '',
      whatsapp: pc.whatsapp || '',
      phone: '',
      consentToSend: pc.consentToSend ?? false,
      consentToWhatsapp: false,
      source: 'legacy',
    };
  }

  return null;
}

/* ==================================================================
   GUARDIAN VALIDATION
   ================================================================== */

export interface GuardianValidationError {
  field: string;
  message: string;
}

/**
 * Validate a guardian record. Returns an array of errors (empty = valid).
 */
export function validateGuardian(g: Partial<Guardian>): GuardianValidationError[] {
  const errors: GuardianValidationError[] = [];
  if (!g.fullName?.trim()) {
    errors.push({ field: 'fullName', message: 'Full name is required.' });
  }
  if (!g.role) {
    errors.push({ field: 'role', message: 'Relationship is required.' });
  }
  if (g.role === 'other' && !g.roleLabel?.trim()) {
    errors.push({ field: 'roleLabel', message: 'Please specify the relationship.' });
  }
  if (g.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(g.email.trim())) {
    errors.push({ field: 'email', message: 'Invalid email address.' });
  }
  return errors;
}

/**
 * Validate that exactly one guardian is marked as primary.
 * Returns an error message or null if valid.
 */
export function validatePrimaryContact(guardians: Guardian[]): string | null {
  if (guardians.length === 0) {
    return 'At least one guardian is required.';
  }
  const primaries = guardians.filter((g) => g.isPrimaryContact);
  if (primaries.length === 0) {
    return 'One guardian must be marked as the primary contact.';
  }
  if (primaries.length > 1) {
    return 'Only one guardian can be the primary contact.';
  }
  return null;
}

/* ==================================================================
   GUARDIAN ROLE DISPLAY
   ================================================================== */

export const GUARDIAN_ROLE_LABELS: Record<string, string> = {
  father: 'Father',
  mother: 'Mother',
  guardian: 'Legal Guardian',
  stepfather: 'Stepfather',
  stepmother: 'Stepmother',
  grandparent: 'Grandparent',
  other: 'Other',
};

export function getGuardianRoleLabel(g: Guardian): string {
  if (g.role === 'other' && g.roleLabel) return g.roleLabel;
  return GUARDIAN_ROLE_LABELS[g.role] ?? g.role;
}
